import type { Config } from 'mixpanel-browser';
import mixPanel from 'mixpanel-browser';

import { MIXPANEL_TOKEN } from '@chk/env-variables';
import { captureException } from '@utility/checkout-utils';

import type { DataLayerIntegrationInterface } from '../DataLayer/DataLayer.types';

import type {
  InitMixpanel,
  RegisterEventProperties,
  TrackEvent,
  TrackEventMethodParams,
  TrackIdentification,
  TrackView,
} from './Mixpanel.types';

const DEFAULT_MIXPANEL_CONFIG = {
  api_host: 'https://mp.volt.io',
  batch_size: 1,
  batch_flush_interval_ms: 1000,
  batch_request_timeout_ms: 15000,
  secure_cookie: true,
  cookie_expiration: 1,
  disable_persistence: true,
  ignore_dnt: true,
};

export const initMixpanel: InitMixpanel = (customMixpanelConfig) => {
  const token = MIXPANEL_TOKEN;

  try {
    if (!token) {
      return;
    }
    const debugMode = token === 'DUMMY_TOKEN';

    mixPanel.init(token, {
      debug: debugMode,
      ...DEFAULT_MIXPANEL_CONFIG,
      ...customMixpanelConfig,
    });
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
};

export const trackEvent: TrackEvent = (name, properties, options) => {
  try {
    mixPanel.track(name, properties, options);
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
};

export const trackIdentification: TrackIdentification = (identity) => {
  try {
    mixPanel.identify(identity);
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
};

export const trackView: TrackView = (name) => {
  try {
    mixPanel.track(`[Page view] ${name}`);
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
};

export const registerGlobalProperties: RegisterEventProperties = (
  properties
) => {
  try {
    mixPanel.register(properties);
  } catch (error) {
    if (error instanceof Error) {
      captureException(error);
    }
  }
};

export class MixpanelIntegration implements DataLayerIntegrationInterface {
  private config = Object.assign({}, DEFAULT_MIXPANEL_CONFIG);
  private static instance: MixpanelIntegration | null = null;

  private constructor(config?: Partial<Config>) {
    Object.assign(this.config, config);
  }

  public static getInstance(config?: Partial<Config>): MixpanelIntegration {
    MixpanelIntegration.instance ??= new MixpanelIntegration(config);

    return MixpanelIntegration.instance;
  }

  public init(): void {
    initMixpanel(this.config);
  }

  public trackEvent({
    name,
    properties,
    options,
  }: TrackEventMethodParams): void {
    trackEvent(name, properties, options);
  }

  public trackIdentification(identity: string): void {
    trackIdentification(identity);
  }

  public trackView(name: string): void {
    trackView(name);
  }

  public addGlobalEventProperties(properties: Record<string, unknown>): void {
    registerGlobalProperties(properties);
  }
}
